import React, { ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { TemporaryDataType, TemporarySetDataType } from '../temporary-data-table/useTemporaryDataState'
import { useDataTable } from '../hooks/useDataTable'
import { someExpandedRows } from '../utils/utils'
import { fetchUnitOptions } from '../services/api'
import { UnitsTypes } from '../enums/units'

export interface StaticDataImporterProviderProps {
    children?: ReactNode
}

export type AssetIdentification = {
    id: string
    name: string
}
export interface StaticDataImporterContextProps {
    data: TemporaryDataType
    setData: TemporarySetDataType
    checkedRows: Record<string, Entity>
    setCheckedRows: React.Dispatch<React.SetStateAction<Record<string, Entity>>>
    checkedToTransfer: TemporaryDataType
    setCheckedToTransfer: (value: TemporaryDataType) => void
    formatCheckedRows: (currentCheckedRows: Record<string, Entity>) => TemporaryDataType
    isValidToTransfer: boolean
    setIsValidToTransfer: React.Dispatch<React.SetStateAction<boolean>>
    handleCheckedToTransfer: (key: string, selectedRows: Entity) => void
    updateValueFromCheckedEntity: (key: string, entityRow: EntityDto) => void
    liveRigs: AssetIdentification[]
    liveWells: AssetIdentification[]
    isFetching: boolean
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>
    temporaryData: TemporaryData[]
    setTemporaryData: React.Dispatch<React.SetStateAction<TemporaryData[]>>
    optionsOfWellEntity: WellSelectOptions
    initialExpandedRowKeys: ExpandedRowKeys
    expandedRowKeys: ExpandedRowKeys
    setExpandedRowKeys: React.Dispatch<React.SetStateAction<ExpandedRowKeys>>
    setExpandedRowKeysByEntity: (entity: keyof ExpandedRowKeys, keys: React.Key[]) => void
    hasSomeExpandedRows: boolean
    unitOptions: Uom[]
}

export const StaticDataImporterContext = createContext<StaticDataImporterContextProps>(
    {} as StaticDataImporterContextProps
)

export const StaticDataImporterProvider = ({ children }: StaticDataImporterProviderProps): JSX.Element => {
    const [checkedRows, setCheckedRows] = useState<Record<string, Entity>>(null)
    const [checkedToTransfer, setCheckedToTransfer] = useState<TemporaryDataType>(null)
    const [isValidToTransfer, setIsValidToTransfer] = useState(false)
    const [temporaryData, setTemporaryData] = useState<TemporaryData[]>([])
    const [unitOptions, setUnitOptions] = useState<Uom[]>([])

    const { data, setData, liveRigs, liveWells, isFetching, setIsFetching, optionsOfWellEntity } = useDataTable(
        temporaryData,
        setTemporaryData
    )

    useEffect(() => {
        fetchUnitOptions(UnitsTypes.LENGTH).then(u => setUnitOptions(u?.options))
    }, [])

    const formatCheckedRows = (currentCheckedRows: Record<string, Entity>): TemporaryDataType => {
        const newCheckedToTransfer = {} as TemporaryDataType

        for (const key in currentCheckedRows) {
            const entityName = key.split('=>')[0]

            if (!newCheckedToTransfer[entityName]) {
                newCheckedToTransfer[entityName] = []
            }

            newCheckedToTransfer[entityName] = [...newCheckedToTransfer[entityName], ...currentCheckedRows[key]]
        }

        setCheckedToTransfer(newCheckedToTransfer)

        return newCheckedToTransfer
    }

    const handleCheckedToTransfer = (key: string, selectedRows: Entity): void => {
        const newCheckedToTransfer = {
            ...checkedRows,
            [key]: selectedRows
        }

        formatCheckedRows(newCheckedToTransfer)
        setCheckedRows(newCheckedToTransfer)
        setIsValidToTransfer(false)
    }

    const updateValueFromCheckedEntity = (key: string, entityRow: EntityDto): void => {
        const newCheckedToTransfer: Record<string, Entity> = { ...checkedRows }
        if (!newCheckedToTransfer[key]) return
        const checkedIndex = newCheckedToTransfer[key].findIndex(checked => checked.frontUuid === entityRow.frontUuid)
        if (checkedIndex === -1) return

        newCheckedToTransfer[key][checkedIndex] = entityRow
        formatCheckedRows(newCheckedToTransfer)
        setCheckedRows(newCheckedToTransfer)
    }

    const initialExpandedRowKeys: ExpandedRowKeys = {
        wells: [],
        interventions: [],
        sections: [],
        runs: [],
        tubulars: [],
        rigTables: []
    }

    const [expandedRowKeys, setExpandedRowKeys] = useState<ExpandedRowKeys>(initialExpandedRowKeys)

    const setExpandedRowKeysByEntity = useCallback(
        (entity: keyof ExpandedRowKeys, keys: React.Key[]): void => {
            setExpandedRowKeys(prevState => ({
                ...prevState,
                [entity]: keys
            }))
        },
        [setExpandedRowKeys]
    )

    const expandedRowKeysMemo = useMemo(() => expandedRowKeys, [expandedRowKeys])

    const value = useMemo(() => {
        const value: StaticDataImporterContextProps = {
            data,
            setData,
            checkedRows,
            setCheckedRows,
            checkedToTransfer,
            setCheckedToTransfer,
            formatCheckedRows,
            isValidToTransfer,
            setIsValidToTransfer,
            handleCheckedToTransfer,
            updateValueFromCheckedEntity,
            liveRigs,
            liveWells,
            isFetching,
            setIsFetching,
            temporaryData,
            setTemporaryData,
            optionsOfWellEntity,
            initialExpandedRowKeys,
            expandedRowKeys: expandedRowKeysMemo,
            setExpandedRowKeys,
            setExpandedRowKeysByEntity,
            hasSomeExpandedRows: someExpandedRows(expandedRowKeys),
            unitOptions
        }

        return value
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, checkedRows, checkedToTransfer, isValidToTransfer, isFetching, unitOptions])

    return <StaticDataImporterContext.Provider value={value}>{children}</StaticDataImporterContext.Provider>
}
